import React from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import { Box, Button, CardHeader, makeStyles, Typography } from '@material-ui/core';

// Assets
import card from '../../../assets/icons/card.svg';
import WPIcon from "../../../assets/icons/whatsapp-primary-dark.svg";
import calendar from '../../../assets/icons/calendar2.svg';
import { formatNumber } from '../../../constants.js/misc';
import {
    getNormalizedPaymentStatusLabel,
    getOrderOrigin,
    getPaymentChannelName,
    isOnlinePaymentOrder,
} from '../../../utils/orderOrigin';

/** Unit price from the order line snapshot; falls back to catalog only when missing. */
const resolveOrderLineUnitPrice = (item) => {
    if (item?.price != null && item?.price !== '') {
        const stored = Number(item.price);
        if (!Number.isNaN(stored) && stored >= 0) {
            return stored;
        }
    }

    const catalog =
        item?.product?.discount_price ??
        item?.product?.discountPrice ??
        item?.product?.price ??
        0;
    const fallback = Number(catalog);
    return Number.isNaN(fallback) ? 0 : fallback;
};

// styles
const useStyles = makeStyles({
    div: {
        background: '#F8F9FB',
        borderRadius: '8px',
        padding: '32px 8px 18px 8px',
        marginBottom: '3px',
        textAlign: 'center',
    },
    div2: {
        border: '1px solid #C9CED6',
        borderRadius: '8px'
    },
    info: {
        background: '#EFF3EF',
        padding: '25px',
        display: 'flex',
        alignItems: 'center'
    },
    buttons: {
        display: 'flex',
        justifyContent: 'flex-end'
    },
    bill: {
        border: '1px solid #C9CED6',
        padding: '50px',
        marginTop: '15px'
    },
    footer: {
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: '30px',
        marginTop: '200px',
        borderTop: '1px solid #C9CED6'
    }
});

const OrderPaymentInfo = ({ info, clientName, clientPhone, orderDetails, store, idOrder, orderShippingCost, couponCode, couponDiscount, order }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const origin = getOrderOrigin(order);
    const gatewayName = getPaymentChannelName(order) || info?.payment_channel?.name;
    const paymentStatus = getNormalizedPaymentStatusLabel(info);
    const amountValue = Number(info?.amount);
    const hasAmount = info?.amount != null && info?.amount !== '' && !Number.isNaN(amountValue);
    const paymentDate = info?.transaction_date ? new Date(info.transaction_date) : null;
    const hasValidDate = paymentDate && !Number.isNaN(paymentDate.getTime());

    const totalWithShipping = () => {
        return orderShippingCost
            ? (isNaN(orderShippingCost)
                ? ((isNaN(store?.delivery_cost_details) || !store?.delivery_cost_enabled) ? 0 : parseInt(store?.delivery_cost_details))
                : orderShippingCost
            )
            : (isNaN(store?.delivery_cost_details) || !store?.delivery_cost_enabled) ? 0 : parseInt(store?.delivery_cost_details);
    }

    const products = orderDetails.map((item) => {
        let product = '';
        if (item?.order_item_option?.length) {
            product = item?.order_item_option.reduce((acc, current) => {
                if (current?.product_option_value) {
                    const currentProduct = current?.product_option_value?.name;
                    return acc + `${currentProduct}, `;
                }
                return '';
            }, '');
        }
        const unitPrice = resolveOrderLineUnitPrice(item);
        const priceLabel =
            unitPrice > 0 ? `_${formatNumber(unitPrice, store?.currency)}_` : '';
        return `- ${item?.quantity} *${item?.product?.name} ${item?.product?.sku}* ${product ? '- ' + product : ''}/  ${priceLabel} \n`;
    });

    const productSubtotal = orderDetails.reduce((prev, item) => {
        const unitPrice = resolveOrderLineUnitPrice(item);
        const qty = Number(item?.quantity) || 0;
        return prev + unitPrice * qty;
    }, 0);

    const shippingCost = totalWithShipping();
    const discount = Math.max(0, Number(couponDiscount) || 0);
    const grandTotal = Math.max(0, productSubtotal + shippingCost - discount);
    const hasCoupon = discount > 0;

    const message = `
Hola, ${clientName},
Este es tu pedido en ${store?.name}:
========================

${products.map(product => product.trim()).join('\n')}

========================
*Subtotal productos:* _${formatNumber(productSubtotal, store?.currency)}_
*Envío:* _${formatNumber(shippingCost, store?.currency)}_
${hasCoupon ? `*Cupón${couponCode ? ` (${couponCode})` : ''}:* _- ${formatNumber(discount, store?.currency)}_\n` : ''}*TOTAL:* _${formatNumber(grandTotal, store?.currency)}_
${store?.shipping_time_enabled ? `*Tiempo de entrega:* ${store?.shipping_details || ``}` : ``}
========================

Tus datos:
*Nombre:* ${clientName}
*Celular:* +${clientPhone}

========================
Realiza el pago a través del siguiente enlace:
https://${store?.url}.ecometri.shop/checkout/${store?.id}/${idOrder}/payment-link
========================
`;


    const handleClick = () => {
        const messageUrl = `https://wa.me/${clientPhone}?text=`
        window.open(`${messageUrl}${encodeURIComponent(message).replace(/%20/g, '+')}`)
    }

    return (
        <Card elevation={2} style={{ marginBottom: '21px' }}>
            <CardHeader titleTypographyProps={{ variant: 'h6' }} title={t('OrderDetail.paymentInfoTitle')} />
            <CardContent style={{ padding: '0 16px 13px 16px' }}>
                <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">{t('OrderDetail.origin.channel')}</Typography>
                    <Typography variant="body1" style={{ fontWeight: 700 }}>{origin.channelLabel}</Typography>
                    {origin.isGateway ? (
                        <>
                            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>{t('OrderDetail.origin.gateway')}</Typography>
                            <Typography variant="body1" style={{ fontWeight: 700 }}>{origin.primaryLabel}</Typography>
                            {origin.branchName && (
                                <Typography variant="caption" color="textSecondary">
                                    {t('OrderDetail.origin.branch')}: {origin.branchName}
                                </Typography>
                            )}
                        </>
                    ) : (
                        <>
                            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>{t('OrderDetail.origin.branch')}</Typography>
                            <Typography variant="body1" style={{ fontWeight: 700 }}>{origin.primaryLabel}</Typography>
                        </>
                    )}
                </Box>
                {info
                    ? <Box className={classes.div2}>
                        <Grid
                            container
                            alignItems="center"
                            justifyContent="center"
                            style={{ padding: '22px 28px' }}>
                            <Grid item xs={2} style={{ padding: '0 10px 0 0' }}>
                                {info?.payment_channel?.icon && (
                                    <img src={info?.payment_channel?.icon} alt={`Icon ${info?.payment_channel?.name}`} width='80px' />
                                )}
                            </Grid>
                            <Grid item xs={10}>
                                <Typography variant="body1" style={{ fontWeight: 'bold' }}>{t('OrderDetail.paymentInfo.name')} {gatewayName || info?.payment_channel?.name}</Typography>
                                {paymentStatus && (
                                    <Typography variant="body1">{t('OrderDetail.paymentInfo.status')} {paymentStatus}</Typography>
                                )}
                                {hasAmount && (
                                    <Typography variant="body1">
                                        {t('OrderDetail.paymentInfo.amount')} {formatNumber(amountValue, info?.currency || store?.currency)}
                                    </Typography>
                                )}
                                {hasValidDate && (
                                    <Typography variant="body1" >
                                        <span>
                                            <img src={calendar} alt="calendar" style={{ width: '14px', marginRight: '7px' }} />
                                        </span>
                                        {t('OrderDetail.paymentInfo.date')} {paymentDate.toLocaleString()}
                                    </Typography>
                                )}
                                {info?.platform_reference && (
                                    <Typography variant="body1">{t('OrderDetail.paymentInfo.reference')} {info?.platform_reference}</Typography>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                    : <Box className={classes.div}>
                        <img src={card} alt="card" style={{ width: '23px' }} />
                        {isOnlinePaymentOrder(order) && gatewayName && (
                            <Typography variant="body1" style={{ fontWeight: 700, marginBottom: 8 }}>
                                {t('OrderDetail.origin.gateway')}: {gatewayName}
                            </Typography>
                        )}
                        <Typography variant="body1">{t('OrderDetail.paymentInfo.notFound')}</Typography>
                        <Button
                            startIcon={<img src={WPIcon} alt='WPIcon' />}
                            color="primary"
                            variant="contained"
                            onClick={handleClick}
                            style={{
                                marginTop: 7,
                                fontSize: 16
                            }}>
                            Enviar enlace de pago
                        </Button>
                    </Box>
                }
            </CardContent>
        </Card >
    );
};
export default connect()(OrderPaymentInfo);
